import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
    AdvancedFilterFieldTypesEnum,
    AdvanceFilterParamType,
    IAdvancedFilterListItem
} from '../../../shared/advanced-filters/components/advance-filter';
import { IFlattenedCategories } from '../../categories.dto';
import {
    DocumentGeneralDataTypeEnum,
    IMetadataItemDto,
    MetaDataTypeEnum,
    OrganizationDataTypeEnum,
    PersonDataTypeEnum
} from '../../settings/documentfields/metadata-item.dto';
import { HttpActionState } from '../../users/userslisting/store/users-listing.reducers';
import { OwnerFilterType } from '../dto/document-listing.dto';
import * as categoriesSelectors from './../../../../store/selectors/categories.selector';
import { DocumentsListingFeatureKey } from './document-listing.config';
import { columnSettingsAdapter, documentListingAdapter, documentsTypeAdapter, IDocumentListingState } from './document-listing.reducers';

const { selectAll, selectEntities } = documentListingAdapter.getSelectors();
const { selectAll: selectAllDocumentTypes } = documentsTypeAdapter.getSelectors();

export const state = createFeatureSelector<IDocumentListingState>(DocumentsListingFeatureKey);

export const activeView = createSelector(state, state => state.viewVariant);
export const isLoading = createSelector(state, (state => state.requestState === HttpActionState.Busy));
export const isLoadingDone = createSelector(state, (state => state.requestState === HttpActionState.Done));
export const documentsSelectors = createSelector(state, state => state.documents);
export const selection = createSelector(state, state => state.selection);

export const documents = createSelector(documentsSelectors, selectAll);
export const documentsByIdSelectors = createSelector(documentsSelectors, selectEntities);
export const paging = createSelector(state, state => state.paging);
export const search = createSelector(state, state => state.search);
export const documentDataLoader = createSelector(state, state => state.documentLoading);
export const getSelectedDocument = createSelector(state, state => state.documentData);
export const documentIndexData = createSelector(state, state => state.documentIndexData);
export const documentIndexDataRequestState = createSelector(state, state => state.documentIndexDataRequestState);

export const contractData = createSelector(state, state => state.contractData);
export const specialAction = createSelector(state, state => state.documentData?.document_special_action || null);

/*
Filters
 */

export const filterBy = createSelector(state, state => state.filter);
export const filterByStatus = createSelector(filterBy, filter => filter?.status || null);

export const documentTypeSelectors = createSelector(state, state => state.documentTypes)
export const documentTypes = createSelector(documentTypeSelectors, selectAllDocumentTypes);
export const isDocumentTypesLoading = createSelector(state, (state) => state?.documentTypesRequestState === HttpActionState.Busy);

export const ownerFilterDDL = createSelector(state, state => ([
    ...state.users.users.map(y => ({
        group: OwnerFilterType.user,
        name: y.name,
        id: y.id
    }))
]));
export const userGroupsSelectors = createSelector(state, state => state.users);

export const userGroups = createSelector(userGroupsSelectors, state => state.groups);

export const isDeletingDocumentsInProgress = createSelector(state, state => state.documentsDeletesRequestState === HttpActionState.Busy);
export const isRestoringDocumentsInProgress = createSelector(state, state => state.documentsRestoreRequestState === HttpActionState.Busy);

export const allColumnsTableSettingsSelector = createSelector(state, state => state.tableSettings?.column_settings);
export const orderedColumnsTableSettingsSelector = createSelector(state, state => state.tableSettings?.order_settings);

const { selectAll: selectAllColumn } = columnSettingsAdapter.getSelectors();
export const allColumns = createSelector(allColumnsTableSettingsSelector, selectAllColumn);
export const orderedColumns = createSelector(orderedColumnsTableSettingsSelector, selectAllColumn);

export const loadingColumnsSettings = createSelector(state, state=>state.fetchCustomColumnsRequestState === HttpActionState.Busy);

export const advancedFilterList = createSelector(state, state => createFilterList(state.metadata?.data, state.advancedFilterSearch));
export const isAdvancedFiltersListLoading = createSelector(state, state => state.advancedFilterRequestState === HttpActionState.Busy);
export const advancedFilterSearch = createSelector(state, state => state.advancedFilterSearch);
export const advancedFilterPage = createSelector(state, state => state.advancedFilterPage);
const allCategoryOption: IFlattenedCategories = {
    id: null,
    category_name: 'All',
    updated_at: null,
    parent_category: null,
    sub_category: null,
    created_at: null,
    user_id: null,
    workspace_id: null,
    is_sub: 0
};
const noCategoryOption: IFlattenedCategories = {
    id: -1,
    category_name: 'No category',
    updated_at: null,
    parent_category: null,
    sub_category: null,
    created_at: null,
    user_id: null,
    workspace_id: null,
    is_sub: 0
};
export const categories = createSelector(categoriesSelectors.allCategories, state => ([
    allCategoryOption,
    noCategoryOption,
    ...(state ?? [])
]));

export const categoriesByDocId = createSelector(state, state => state.categoriesByDocId);

export const filters = createSelector(state, state => state.appliedAdvancedFilters);
export const appliedAdvancedFilters = createSelector(filters, x => x);
export const lastSelectedDocument = createSelector(state, documentsByIdSelectors, (state, documents) => state.lastSelectedDocumentId ? documents[state.lastSelectedDocumentId] : null)
export const ownerFilterSearch = createSelector(state, state => state.ownerFilterSearch);

function matchIconFor(y: IMetadataItemDto): string {
    switch (y.meta_data_type) {
        case MetaDataTypeEnum.text:
            return 'type';
        case MetaDataTypeEnum.droplist:
            return 'droplet';
        case MetaDataTypeEnum.checkbox:
            return 'check-square'
        case MetaDataTypeEnum.datepicker:
            return 'calendar';
    }

    return '';
}

function createFilterList(data: IMetadataItemDto[] | undefined, advancedFilterSearch: string) {


    return [
        ...constAdvancedFilters.filter(x => advancedFilterSearch?.trim().length > 0 ? x.text?.toLowerCase().indexOf(advancedFilterSearch.toLowerCase()) > 0 : true),
        ...(data?.filter(x => x.section === AdvancedFilterFieldTypesEnum.document_field)
            .map(y => ({
                group: y.section,
                text: y.meta_data_name,
                icon: matchIconFor(y),
                field_type: y.section as AdvancedFilterFieldTypesEnum,
                source: y
            })) || [])
    ];
}

export const constAdvancedFilters: IAdvancedFilterListItem<AdvanceFilterParamType>[] = [
    {
        group: AdvancedFilterFieldTypesEnum.general_field,
        text: "Signing Date",
        icon: 'calendar',
        field_type: AdvancedFilterFieldTypesEnum.general_field,
        source: {
            general_data_type: DocumentGeneralDataTypeEnum.signing_date
        } as AdvanceFilterParamType,
    },
    {
        group: AdvancedFilterFieldTypesEnum.general_field,
        text: "Template name",
        icon: 'edit',
        field_type: AdvancedFilterFieldTypesEnum.general_field,
        source: {
            general_data_type: DocumentGeneralDataTypeEnum.template_name
        } as AdvanceFilterParamType,
    },

    {
        group: AdvancedFilterFieldTypesEnum.person_field,
        text: "Full name",
        icon: 'user',
        field_type: AdvancedFilterFieldTypesEnum.person_field,
        source: {
            person_data_type: PersonDataTypeEnum.FullName
        } as AdvanceFilterParamType
    },
    {
        group: AdvancedFilterFieldTypesEnum.person_field,
        text: "Last name",
        icon: 'user',
        field_type: AdvancedFilterFieldTypesEnum.person_field,
        source: {
            person_data_type: PersonDataTypeEnum.LastName
        } as AdvanceFilterParamType
    },
    {
        group: AdvancedFilterFieldTypesEnum.person_field,
        text: "Email",
        icon: 'mail',
        field_type: AdvancedFilterFieldTypesEnum.person_field,
        source: {
            person_data_type: PersonDataTypeEnum.Email
        } as AdvanceFilterParamType
    },
    {
        group: AdvancedFilterFieldTypesEnum.person_field,
        text: "Personal ID number",
        icon: 'user',
        field_type: AdvancedFilterFieldTypesEnum.person_field,
        source: {
            person_data_type: PersonDataTypeEnum.PersonalIDNumber
        } as AdvanceFilterParamType
    },
    {
        group: AdvancedFilterFieldTypesEnum.person_field,
        text: "Mobile",
        icon: 'phone',
        field_type: AdvancedFilterFieldTypesEnum.person_field,
        source: {
            person_data_type: PersonDataTypeEnum.PhoneNo
        } as AdvanceFilterParamType
    },
    {
        group: AdvancedFilterFieldTypesEnum.person_field,
        text: "Address",
        icon: 'home',
        field_type: AdvancedFilterFieldTypesEnum.person_field,
        source: {
            person_data_type: PersonDataTypeEnum.Address
        } as AdvanceFilterParamType
    },
    {
        group: AdvancedFilterFieldTypesEnum.person_field,
        text: "City",
        icon: 'home',
        field_type: AdvancedFilterFieldTypesEnum.person_field,
        source: {
            person_data_type: PersonDataTypeEnum.City
        } as AdvanceFilterParamType
    },
    {
        group: AdvancedFilterFieldTypesEnum.person_field,
        text: "Zip code",
        icon: 'inbox',
        field_type: AdvancedFilterFieldTypesEnum.person_field,
        source: {
            person_data_type: PersonDataTypeEnum.ZipCode
        } as AdvanceFilterParamType
    },

    {
        group: AdvancedFilterFieldTypesEnum.organization_field,
        text: "Name",
        icon: 'briefcase',
        field_type: AdvancedFilterFieldTypesEnum.organization_field,
        source: {
            organization_data_type: OrganizationDataTypeEnum.Organization,
        } as AdvanceFilterParamType
    },

    {
        group: AdvancedFilterFieldTypesEnum.organization_field,
        text: "Reg_no",
        icon: 'briefcase',
        field_type: AdvancedFilterFieldTypesEnum.organization_field,
        source: {
            organization_data_type: OrganizationDataTypeEnum.RegNo,
        } as AdvanceFilterParamType
    },

    {
        group: AdvancedFilterFieldTypesEnum.organization_field,
        text: "Address",
        icon: 'home',
        field_type: AdvancedFilterFieldTypesEnum.organization_field,
        source: {
            organization_data_type: OrganizationDataTypeEnum.Address,
        } as AdvanceFilterParamType
    },

    {
        group: AdvancedFilterFieldTypesEnum.organization_field,
        text: "City",
        icon: 'home',
        field_type: AdvancedFilterFieldTypesEnum.organization_field,
        source: {
            organization_data_type: OrganizationDataTypeEnum.City,
        } as AdvanceFilterParamType
    },

    {
        group: AdvancedFilterFieldTypesEnum.organization_field,
        text: "Zip code",
        icon: 'inbox',
        field_type: AdvancedFilterFieldTypesEnum.organization_field,
        source: {
            organization_data_type: OrganizationDataTypeEnum.ZipCode,
        } as AdvanceFilterParamType
    },

    {
        group: AdvancedFilterFieldTypesEnum.organization_field,
        text: "Mobile no",
        icon: 'phone',
        field_type: AdvancedFilterFieldTypesEnum.organization_field,
        source: {
            organization_data_type: OrganizationDataTypeEnum.PhoneNo,
        } as AdvanceFilterParamType
    },
];





/*Legal hub*/
export const selectLegalHubFilters = createSelector(
    state,
    state => state.legalHub?.filters ?? null
)

export const selectedLegalHubPlaybook = createSelector(
    state,
    state => state.legalHub?.selectedPlaybook ?? null
)

export const rightBarHigherZindex = createSelector(
    state,
    state => state.rightBarHigherZindex ?? false
)
